import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  Tooltip,
  Input,
  DatePicker,
  Row,
  Col,
  Modal,
  Descriptions,
  message,
} from "antd";
import {
  EyeOutlined,
  FileTextOutlined,
  SearchOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import moment from "moment";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

function LabHistoryManager() {
  const { request } = useRequest();

  // State
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Filter states
  const [search, setSearch] = useState("");
  const [dates, setDates] = useState(null); // [moment, moment]
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [reportDetails, setReportDetails] = useState([]);

  // File preview states
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const handlePreview = (record) => {
    setPreviewData(record);
    setPreviewVisible(true);
  };

  const handleDownload = async () => {
    if (!previewData?.report_file) return;
    try {
      const response = await fetch(previewData.report_file);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = previewData.report_file_name || `report_${previewData.id}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error("Download failed, opening in new tab instead:", error);
      window.open(previewData.report_file, '_blank');
    }
  };

  // Fetch history with filters
  const fetchHistory = useCallback(
    (page = 1, limit = 10, searchTerm = "", dateRange = null) => {
      setLoading(true);
      let url = `/lab/report_upload_history?page=${page}&limit=${limit}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (dateRange && dateRange[0] && dateRange[1]) {
        url += `&date_from=${dateRange[0].format("YYYY-MM-DD")}`;
        url += `&date_to=${dateRange[1].format("YYYY-MM-DD")}`;
      }

      request({
        url,
        method: "GET",
        onSuccess: (data) => {
          setLoading(false);
          let historyData = [];
          let total = 0;
          if (data?.data && Array.isArray(data.data)) {
            historyData = data.data;
            total = data.pagination?.total || historyData.length;
          } else if (Array.isArray(data)) {
            historyData = data;
            total = data.length;
          }
          setHistory(historyData);
          setPagination((prev) => ({
            ...prev,
            current: page,
            pageSize: limit,
            total: total,
          }));
        },
        onError: (error) => {
          setLoading(false);
          setHistory([]);
          ShowToast(
            error.response?.data?.message || "Failed to fetch history",
            Severty.ERROR,
          );
        },
      });
    },
    [request],
  );

  // Fetch report details for given IDs
  const fetchReportDetails = (reportIds) => {
    setLoadingDetail(true);
    request({
      url: `/lab/reports/bulk-detail/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: { report_ids: reportIds },
      onSuccess: (data) => {
        setLoadingDetail(false);
        // data structure may be { status, data: [...] }
        const reports = data?.data || data;
        setReportDetails(Array.isArray(reports) ? reports : []);
        setDetailModalVisible(true);
      },
      onError: (error) => {
        setLoadingDetail(false);
        ShowToast(
          error.response?.data?.message || "Failed to fetch report details",
          Severty.ERROR,
        );
      },
    });
  };

  // Handle search button click
  const handleSearch = () => {
    fetchHistory(1, pagination.pageSize, search, dates);
  };

  // Handle date range change
  const handleDateChange = (dates) => {
    setDates(dates);
  };

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    fetchHistory(
      paginationInfo.current,
      paginationInfo.pageSize,
      search,
      dates,
    );
  };

  // Reset filters
  const handleReset = () => {
    setSearch("");
    setDates(null);
    fetchHistory(1, pagination.pageSize, "", null);
  };

  // Initial load
  useEffect(() => {
    fetchHistory(pagination.current, pagination.pageSize, search, dates);
  }, []); // run once

  // Columns for history table
  const columns = [
    {
      title: "S. No.",
      key: "serial_no",
      width: 70,
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },

    {
      title: "Patient",
      key: "patient",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.patient_name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.patient_email}
          </Text>
        </Space>
      ),
    },
    {
      title: "Patient Mobile",
      dataIndex: "patient_mobile",
      key: "patient_mobile",
      render: (mobile, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{mobile}</Text>
        </Space>
      ),
    },
    {
      title: "Report IDs",
      dataIndex: "report_ids",
      key: "report_ids",
      render: (ids) => (
        <Tooltip title={ids.join(", ")}>
          <Tag color="blue">{ids.length} report(s)</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Total Reports",
      dataIndex: "total_reports",
      key: "total_reports",
      align: "center",
      render: (total) => <Tag color="green">{total}</Tag>,
    },
    {
      title: "Uploaded At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => moment(date).format("DD MMM YYYY, hh:mm A"),
      sorter: (a, b) =>
        moment(a.created_at).unix() - moment(b.created_at).unix(),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => fetchReportDetails(record.report_ids)}
        />
      ),
    },
  ];

  // Column definitions for report details modal (inside modal)
  const detailColumns = [
    {
      title: "Report ID",
      dataIndex: "id",
      key: "id",
      width: 100,
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category_name",
      render: (name) => name || "N/A",
    },
    {
      title: "File Name",
      dataIndex: "report_file_name",
      key: "report_file_name",
      render: (name) => name || "N/A",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
    },
    {
      title: "Uploaded At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => moment(date).format("DD MMM YYYY, hh:mm A"),
    },
    {
      title: "View File",
      key: "file",
      render: (_, record) =>
        record.report_file ? (
          <Button
            type="primary"
            ghost
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
          />
        ) : (
          "N/A"
        ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <FileTextOutlined style={{ color: "#1890ff" }} />
            <span>Report Upload History (Lab)</span>
          </Space>
        }
      >
        {/* Filters Row */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Search by patient name/email or uploader name/email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col span={8}>
            <RangePicker
              style={{ width: "100%" }}
              placeholder={["From Date", "To Date"]}
              value={dates}
              onChange={handleDateChange}
            />
          </Col>
          <Col span={8}>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                Search
              </Button>
              <Button onClick={handleReset}>Reset</Button>
            </Space>
          </Col>
        </Row>

        <Table scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={history}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} uploads`,
            pageSizeOptions: ["10", "20", "30", "50"],
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Modal for report details */}
      <Modal
        title="Report Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={900}
        confirmLoading={loadingDetail}
      >
        <Table scroll={{ x: 'max-content' }}
          columns={detailColumns}
          dataSource={reportDetails}
          rowKey="id"
          pagination={false}
          loading={loadingDetail}
          size="small"
        />
      </Modal>

      {/* Modal for file preview */}
      <Modal
        title="Report Preview & Details"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
        centered
        bodyStyle={{ padding: "24px" }}
      >
        {previewData && (
          <Row gutter={24}>
            {/* Left Column: Image/PDF Preview */}
            <Col xs={24} md={14}>
              <div style={{ background: "#f5f5f5", padding: 8, borderRadius: 8, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
                {previewData.report_file ? (
                  previewData.report_file.toLowerCase().includes(".pdf") ? (
                    <iframe
                      src={previewData.report_file}
                      title="File Preview"
                      style={{ width: "100%", height: "60vh", border: "none", display: "block" }}
                    />
                  ) : (
                    <img
                      src={previewData.report_file}
                      alt="File Preview"
                      style={{ maxWidth: "100%", height: "auto", maxHeight: "60vh", objectFit: "contain", display: "block" }}
                    />
                  )
                ) : (
                  <Text type="secondary">No file available</Text>
                )}
              </div>
            </Col>

            {/* Right Column: Report Details */}
            <Col xs={24} md={10}>
              <Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>Report Information</Title>
              <Descriptions column={1} bordered size="small" layout="vertical">
                <Descriptions.Item label="Report ID">
                  <Text strong>{previewData.id}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Category">
                  {previewData.category_name || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="File Name">
                  {previewData.report_file_name || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Description">
                  {previewData.description || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Notes">
                  {previewData.notes || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Uploaded At">
                  {moment(previewData.created_at).format("DD MMM YYYY, hh:mm A")}
                </Descriptions.Item>
              </Descriptions>

              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                style={{
                  marginTop: 24,
                  width: "100%",
                  height: 44,
                  borderRadius: 8,
                  background: "#1677ff",
                  fontWeight: 600
                }}
              >
                Download Report
              </Button>
            </Col>
          </Row>
        )}
      </Modal>
    </div>
  );
}

export default LabHistoryManager;
